import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ModuleHeader } from 'components/modals';
import type { ActionButton, ViewModeOption } from 'components/modals';
import { type ActionMenuItem, SyutGrid, type SyutGridColumn, SyutSelect } from 'components/ui';
import { ConfirmDialog } from 'components/modals/common/ConfirmDialog';
import { useSnackbar } from 'components/common/SnackbarNotification';
import { ApprovalActionModal } from 'components/modals/modules/approval-module/ApprovalModal';
import type { ApprovalItem } from 'components/modals/modules/approval-module/ApprovalModal';
import { formatCurrency, formatDate, getStatusBadgeStyle } from 'utils/lmmsHelpers';
import { useApprovalDashboard, useApprovalDetail, useApprovalAction } from 'hooks/api';
import { GridLoadingSkeleton } from 'components/common';
// ============================================================================
// Record type helpers
// ============================================================================

const RECORD_TYPE_LABELS: Record<string, string> = {
  addon_request: 'Add-On Request',
  closing: 'Closing',
  combined: 'Combined',
  regroup: 'Regroup',
  gl_replacement: 'GL Replacement',
};

const RECORD_TYPE_COLORS: Record<string, { bg: string; color: string }> = {
  addon_request: { bg: 'rgba(59, 130, 246, 0.1)', color: '#2563eb' },
  closing: { bg: 'rgba(239, 68, 68, 0.1)', color: '#dc2626' },
  combined: { bg: 'rgba(139, 92, 246, 0.1)', color: '#7c3aed' },
  regroup: { bg: 'rgba(217, 119, 6, 0.1)', color: '#b45309' },
  gl_replacement: { bg: 'rgba(20, 184, 166, 0.1)', color: '#0d9488' },
};

// ============================================================================
// MAIN SCREEN
// ============================================================================

const ApprovalDashboardScreen: React.FC = () => {
  // API hooks
  const { data: apiData, loading: apiLoading, error: apiError, refetch } = useApprovalDashboard();
  const { loading: detailLoading, fetchDetail, reset: resetDetail } = useApprovalDetail();
  const { processAction } = useApprovalAction();
  // Data from API
  const items = (apiData as unknown as ApprovalItem[]) || [];
  const [viewMode, setViewMode] = useState<string>('list');
  const [searchQuery, setSearchQuery] = useState('');
  const [filterOpen, setFilterOpen] = useState(false);
  const [selectedRows, setSelectedRows] = useState<ApprovalItem[]>([]);
  const [actionModalOpen, setActionModalOpen] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState<ApprovalItem | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);

  // Filter state
  const [recordTypeFilter, setRecordTypeFilter] = useState('');
  const [branchFilter, setBranchFilter] = useState('');
  const tabListRef = useRef<HTMLDivElement>(null);

  // Branch options — from mock data items or localStorage
  const branchOptions = useMemo(() => {
    
    try {
      const stored = localStorage.getItem('my_branches');
      if (!stored) return [];
      const branches = JSON.parse(stored) as Array<{ businessUnitId: string; buName: string; buCode: string }>;
      return branches.map((b) => ({ value: b.buName, label: `${b.buName} (${b.buCode})` }));
    } catch {
      return [];
    }
  }, [items]);

  // Confirm dialog state
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean; title: string; message: string; variant: 'default' | 'danger'; onConfirm: () => void;
  }>({ open: false, title: '', message: '', variant: 'default', onConfirm: () => {} });

  const { showMessage } = useSnackbar();

  // ============================================================================
  // SUMMARY COUNTS
  // ============================================================================

  const counts = useMemo(() => ({
    total: items.length,
    addonRequest: items.filter((i) => i.recordType === 'addon_request').length,
    closing: items.filter((i) => i.recordType === 'closing').length,
    combined: items.filter((i) => i.recordType === 'combined').length,
    regroup: items.filter((i) => i.recordType === 'regroup').length,
    glReplacement: items.filter((i) => i.recordType === 'gl_replacement').length,
  }), [items]);

  // ============================================================================
  // FILTERED DATA
  // ============================================================================

  const filteredData = useMemo(() =>
    items.filter((item) => {
      const matchesSearch = !searchQuery ||
        item.referenceNumber.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.customerName.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.customerId.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesType = recordTypeFilter === '' || item.recordType === recordTypeFilter;
      const matchesBranch = branchFilter === '' || (item.branch || '').toLowerCase() === branchFilter.toLowerCase();
      return matchesSearch && matchesType && matchesBranch;
    }),
    [items, searchQuery, recordTypeFilter, branchFilter]
  );

  // ============================================================================
  // HANDLERS
  // ============================================================================

  const handleReview = useCallback((record: ApprovalItem) => {
    setActionModalOpen(true);
    // Set summary data immediately so modal header renders, then fetch full detail
      setSelectedRecord(record);
      fetchDetail(record.recordType, record.id).then((detail) => {
        setSelectedRecord(detail);
      }).catch(() => {
        // Detail fetch failed — modal will show summary-only data
      });

  }, [fetchDetail]);

  const handleApprovalAction = useCallback((action: 'approved' | 'returned' | 'rejected', remark: string) => {
    if (!selectedRecord) return;

    const actionLabels = { approved: 'Approve', returned: 'Return', rejected: 'Reject' };
    const actionLabel = actionLabels[action];
    const isDestructive = action === 'rejected';

    setConfirmDialog({
      open: true,
      title: `${actionLabel} ${selectedRecord.referenceNumber}`,
      message: `Are you sure you want to ${actionLabel.toLowerCase()} this ${RECORD_TYPE_LABELS[selectedRecord.recordType]}?${remark ? `\n\nRemark: "${remark}"` : ''}`,
      variant: isDestructive ? 'danger' : 'default',
      onConfirm: async () => {
        setIsProcessing(true);
        try {
          await processAction({
              recordType: selectedRecord.recordType,
              recordId: selectedRecord.id,
              action,
              remark,
            });
            await refetch();

          const messageMap = {
            approved: `${selectedRecord.referenceNumber} approved successfully`,
            returned: `${selectedRecord.referenceNumber} returned for correction`,
            rejected: `${selectedRecord.referenceNumber} rejected`,
          };
          showMessage(messageMap[action], action === 'rejected' ? 'warning' : 'success');
          setConfirmDialog((prev) => ({ ...prev, open: false }));
          setActionModalOpen(false);
        } catch (err: any) {
          const msg = err?.response?.data?.message || 'Failed to process approval action';
          showMessage(msg, 'error');
          setConfirmDialog((prev) => ({ ...prev, open: false }));
        } finally {
          setIsProcessing(false);
        }
      },
    });
  }, [selectedRecord, showMessage, processAction, refetch]);

  // ============================================================================
  // ROW ACTIONS
  // ============================================================================

  const getRowActions = useCallback((row: ApprovalItem): ActionMenuItem[] => [
    {
      id: 'review',
      label: 'Review & Act',
      icon: <IconSystem.edit size={18} />,
      onClick: () => handleReview(row),
    },
  ], [handleReview]);

  // ============================================================================
  // COLUMN DEFINITIONS
  // ============================================================================

  const tableColumns: SyutGridColumn<ApprovalItem>[] = useMemo(() => [
    {
      id: 'recordType',
      header: 'Type',
      accessorKey: 'recordType',
      size: 150,
      cell: ({ getValue }: any) => {
        const type = getValue();
        const style = RECORD_TYPE_COLORS[type] || RECORD_TYPE_COLORS.addon_request;
        return (
          <span style={{
            padding: '0.2rem 0.5rem',
            borderRadius: '0.25rem',
            fontSize: '0.7rem',
            fontWeight: 600,
            background: style.bg,
            color: style.color,
          }}>
            {RECORD_TYPE_LABELS[type] || type}
          </span>
        );
      },
    },
    { id: 'referenceNumber', header: 'Reference #', accessorKey: 'referenceNumber', size: 170 },
    { id: 'customerName', header: 'Customer', accessorKey: 'customerName', size: 140 },
    {
      id: 'branch',
      header: 'Branch',
      accessorKey: 'branch',
      size: 160,
      cell: ({ getValue }: any) => getValue() || '—',
    },
    {
      id: 'amount',
      header: 'Amount',
      accessorKey: 'amount',
      size: 150,
      cell: ({ row }: any) => {
        const r = row.original;
        if ((r.recordType === 'combined' || r.recordType === 'regroup' || r.recordType === 'gl_replacement') && r.netCashFlow !== undefined) {
          return (
            <span style={{ fontWeight: 700, color: r.netCashFlow >= 0 ? '#16a34a' : '#dc2626' }}>
              {r.netCashFlow >= 0 ? '+' : ''}{formatCurrency(r.netCashFlow)}
            </span>
          );
        }
        return <span style={{ fontWeight: 600 }}>{formatCurrency(r.amount)}</span>;
      },
    },
    {
      id: 'submittedBy',
      header: 'Submitted By',
      accessorKey: 'submittedBy',
      size: 130,
      cell: ({ row }: any) => row.original.submittedByName || row.original.submittedBy,
    },
    {
      id: 'submittedAt',
      header: 'Submitted At',
      accessorKey: 'submittedAt',
      size: 120,
      cell: ({ getValue }: any) => formatDate(getValue()),
    },
    {
      id: 'status',
      header: 'Status',
      accessorKey: 'status',
      size: 150,
      cell: ({ getValue }: any) => {
        const status = getValue();
        return <span style={getStatusBadgeStyle(status)}>{(status ?? '').replace(/_/g, ' ')}</span>;
      },
    },
  ], []);

  // ============================================================================
  // VIEW MODE & ACTION BUTTONS
  // ============================================================================

  const viewModeOptions: ViewModeOption[] = [
    { id: 'list', icon: <IconSystem.list size={16} />, label: 'List View', tooltip: 'List View' },
    { id: 'grid', icon: <IconSystem.grid size={16} />, label: 'Grid View', tooltip: 'Grid View', disabled: true },
    { id: 'calendar', icon: <IconSystem.calendar size={16} />, label: 'Calendar View', tooltip: 'Calendar View (Not Applicable)', disabled: true },
  ];

  const actionButtons: ActionButton[] = [
    { id: 'refresh', icon: <IconSystem.refresh size={18} />, label: 'Refresh', onClick: () => refetch(), tooltip: 'Refresh', variant: 'icon' },
  ];

  // ============================================================================
  // RENDER
  // ============================================================================

  return (
    <div className="module-master-container">
      <ModuleHeader
        visible={true}
        title="Approval Dashboard"
        icon={<IconSystem.checkCircle size={20} />}
        description="Review and act on pending approval items."
        searchConfig={{ value: searchQuery, onChange: setSearchQuery, placeholder: 'Search pending items...' }}
        viewModeConfig={{ currentMode: viewMode, modes: viewModeOptions, onChange: (mode) => setViewMode(mode) }}
        filterConfig={{ onClick: () => setFilterOpen(!filterOpen), isActive: filterOpen }}
        actions={actionButtons}
      />

      <div className="module-content">
        {apiError && items.length === 0 && (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '3rem', color: 'var(--danger)' }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '0.9rem', marginBottom: '0.5rem' }}>Failed to load approval items</div>
              <button className="btn btn-secondary" onClick={() => refetch()} style={{ fontSize: '0.8rem' }}>Retry</button>
            </div>
          </div>
        )}

        {apiLoading && items.length === 0 ? (
          <GridLoadingSkeleton columns={6} />
        ) : (
        <div className="data-grid-container">
          {/* Tab Bar */}
          <div
            role="tablist"
            aria-label="Filter by record type"
            ref={tabListRef}
            onKeyDown={(e) => {
              const tabs = tabListRef.current?.querySelectorAll<HTMLButtonElement>('[role="tab"]');
              if (!tabs || tabs.length === 0) return;
              const currentIndex = Array.from(tabs).findIndex((t) => t === document.activeElement);
              if (currentIndex === -1) return;
              let nextIndex = -1;
              if (e.key === 'ArrowRight') nextIndex = (currentIndex + 1) % tabs.length;
              else if (e.key === 'ArrowLeft') nextIndex = (currentIndex - 1 + tabs.length) % tabs.length;
              else if (e.key === 'Home') nextIndex = 0;
              else if (e.key === 'End') nextIndex = tabs.length - 1;
              if (nextIndex !== -1) {
                e.preventDefault();
                tabs[nextIndex].focus();
                tabs[nextIndex].click();
              }
            }}
            style={{
              display: 'flex',
              background: 'var(--bg-secondary)',
              borderRadius: '0.5rem 0.5rem 0 0',
              borderBottom: '2px solid var(--border-color)',
              marginBottom: '0.5rem',
              padding: '0.25rem 0.25rem 0',
              gap: '0.25rem',
            }}
          >
            {([
              { key: '', label: 'All Pending', count: counts.total, color: 'var(--primary)', activeBg: 'rgba(59, 130, 246, 0.08)' },
              { key: 'addon_request', label: 'Add-On Requests', count: counts.addonRequest, color: '#2563eb', activeBg: 'rgba(59, 130, 246, 0.08)' },
              { key: 'closing', label: 'Closings', count: counts.closing, color: '#dc2626', activeBg: 'rgba(239, 68, 68, 0.08)' },
              { key: 'combined', label: 'Combined', count: counts.combined, color: '#7c3aed', activeBg: 'rgba(139, 92, 246, 0.08)' },
              { key: 'regroup', label: 'Regroup', count: counts.regroup, color: '#b45309', activeBg: 'rgba(217, 119, 6, 0.08)' },
              { key: 'gl_replacement', label: 'GL Replacements', count: counts.glReplacement, color: '#0d9488', activeBg: 'rgba(20, 184, 166, 0.08)' },
            ] as const).map((tab) => {
              const isActive = recordTypeFilter === tab.key;
              return (
                <button
                  key={tab.key}
                  role="tab"
                  aria-selected={isActive}
                  tabIndex={isActive ? 0 : -1}
                  onClick={() => setRecordTypeFilter(tab.key)}
                  style={{
                    position: 'relative',
                    padding: '0.5rem 1.25rem 0.625rem',
                    background: isActive ? 'var(--bg-primary)' : 'transparent',
                    border: 'none',
                    borderRadius: isActive ? '0.375rem 0.375rem 0 0' : '0.375rem 0.375rem 0 0',
                    borderBottom: `2px solid ${isActive ? tab.color : 'transparent'}`,
                    marginBottom: '-2px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    transition: 'all 0.15s ease',
                    opacity: 1,
                    boxShadow: isActive ? '0 -1px 4px rgba(0,0,0,0.06)' : 'none',
                  }}
                >
                  <span style={{
                    fontSize: '0.8rem',
                    fontWeight: isActive ? 600 : 400,
                    color: isActive ? tab.color : 'var(--text-secondary)',
                    whiteSpace: 'nowrap',
                  }}>
                    {tab.label}
                  </span>
                  <span style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minWidth: '1.375rem',
                    height: '1.375rem',
                    padding: '0 0.375rem',
                    borderRadius: '9999px',
                    fontSize: '0.7rem',
                    fontWeight: 700,
                    background: isActive ? tab.activeBg : 'rgba(255,255,255,0.04)',
                    color: isActive ? tab.color : 'var(--text-muted)',
                    border: isActive ? `1px solid ${tab.color}30` : '1px solid var(--border-color)',
                  }}>
                    {tab.count}
                  </span>
                </button>
              );
            })}
          </div>

          <div style={{ flex: 1, overflow: 'hidden', minHeight: 0 }}>
            <SyutGrid
              data={filteredData}
              columns={tableColumns}
              enableRowSelection
              enablePagination
              enableColumnFilters={false}
              rowActions={getRowActions}
              onSelectionChange={setSelectedRows}
              emptyMessage="No pending approval items"
            />
          </div>
        </div>
        )}

        {/* Filter Panel */}
        <div className={`search-panel ${filterOpen ? '' : 'collapsed'}`}>
          <div className="search-panel-header">
            <h3 className="search-panel-title">Filters</h3>
            <button className="search-panel-close" onClick={() => setFilterOpen(false)} title="Close filters">
              <IconSystem.x size={14} />
            </button>
          </div>
          <div className="search-panel-content">
            <div className="filter-section">
              <SyutSelect
                label="Record Type"
                options={[
                  { value: '', label: 'All Types' },
                  { value: 'addon_request', label: 'Add-On Requests' },
                  { value: 'closing', label: 'Closing Transactions' },
                  { value: 'combined', label: 'Combined Transactions' },
                  { value: 'regroup', label: 'Regroup Transactions' },
                  { value: 'gl_replacement', label: 'GL Replacements' },
                ]}
                value={recordTypeFilter}
                onChange={(val: string | string[] | null) => setRecordTypeFilter((val as string) || '')}
                placeholder="Search type..."
                isSearchable={true}
                isClearable={false}
                size="sm"
              />
            </div>

            {branchOptions.length > 1 && (
              <div className="filter-section">
                <SyutSelect
                  label="Branch"
                  options={[{ value: '', label: 'All Branches' }, ...branchOptions]}
                  value={branchFilter}
                  onChange={(val: string | string[] | null) => setBranchFilter((val as string) || '')}
                  placeholder="Search branch..."
                  isSearchable={true}
                  isClearable={false}
                  size="sm"
                />
              </div>
            )}
          </div>
          <div className="filter-actions">
            <button className="btn btn-secondary" onClick={() => { setRecordTypeFilter(''); setBranchFilter(''); }}>Clear</button>
            <button className="btn btn-primary" onClick={() => setFilterOpen(false)}>Apply</button>
          </div>
        </div>
      </div>

      {/* Approval Action Modal */}
      <ApprovalActionModal
        isOpen={actionModalOpen}
        record={selectedRecord}
        detailLoading={detailLoading}
        isProcessing={isProcessing}
        onAction={handleApprovalAction}
        onClose={() => { if (!isProcessing) { setActionModalOpen(false); resetDetail(); } }}
      />

      {/* Confirm Dialog */}
      <ConfirmDialog
        open={confirmDialog.open}
        title={confirmDialog.title}
        message={confirmDialog.message}
        variant={confirmDialog.variant}
        onConfirm={confirmDialog.onConfirm}
        onCancel={() => { if (!isProcessing) setConfirmDialog((prev) => ({ ...prev, open: false })); }}
        confirmLabel={confirmDialog.variant === 'danger' ? 'Reject' : 'Confirm'}
        loading={isProcessing}
      />
    </div>
  );
};

export default ApprovalDashboardScreen;
